import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import {
  GenerationBadge,
  KeyInventoryErrorState,
  UpgradeOffersErrorState,
} from "@/components/GenerationBadge";
import { Money } from "@/components/Money";
import { RelatedLink } from "@/components/RelatedLink";
import { StatusBadge } from "@/components/StatusBadge";
import { WalletDestinationLookup } from "@/components/WalletDestinationLookup";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState, LoadingState } from "@/components/states";
import { col } from "@/entities/columns";
import { COLLECTIONS } from "@/firestore/collections";
import { DOCUMENT_ID, useFirestoreInQuery, usePaginatedQuery } from "@/firestore/hooks";
import type { Filter } from "@/firestore/hooks";
import type {
  SessionKeyPermissionDoc,
  WalletAvailabilityDoc,
  WalletKeyInventoryDoc,
  WalletUpgradeOfferDoc,
  WithDocId,
} from "@/firestore/types";
import { OFFER_LIST_FILTERS, offerEndedAt } from "@/wallet/upgradeOffers";
import { sessionKeyPermissionColumns } from "@/entities/configs/sessionKeyPermissions";
import { formatTimestamp } from "@/lib/datetime";
import { formatMicroUsdc } from "@/lib/format";
import { entityPath } from "@/routes/paths";
import {
  REGISTERED_GENERATIONS,
  joinInventoryBalances,
  sortByExposure,
  totalExposure,
} from "@/wallet/generations";
import type { FleetWalletRow } from "@/wallet/generations";
import { FLEET_WALLET_STATES, fetchGenerationCounts } from "./generations/counts";
import type { CountResult, GenerationCounts } from "./generations/counts";

/** Wallet states whose legacy keys can still move funds the user expects to keep using. */
const LIVE_WALLET_STATES = ["provisioned", "migrating"];

/** Newest-relevant first: the two coexisting generations, then the deprecated ones. */
const TAB_ORDER = [...REGISTERED_GENERATIONS].sort(
  (a, b) => Number(a.deprecated) - Number(b.deprecated) || a.key.localeCompare(b.key),
);

function CountCell({ result }: { result: CountResult | undefined }) {
  if (!result) return <Text c="dimmed">—</Text>;
  if (!result.ok)
    return (
      <Tooltip label={result.error.message} multiline w={320} withArrow>
        <Text c="dimmed" span>
          unavailable
        </Text>
      </Tooltip>
    );
  return (
    <Text span ff="monospace">
      {result.value.toLocaleString()}
    </Text>
  );
}

function countText(result: CountResult): string {
  return result.ok ? result.value.toLocaleString() : "unavailable";
}

function FleetCountsCard({
  data,
  loading,
  refreshing,
  error,
  refresh,
}: {
  data: GenerationCounts | undefined;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  refresh: () => void;
}) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs">
        <div>
          <Text fw={600}>Wallets by generation</Text>
          <Text size="sm" c="dimmed">
            Server counts over the key inventory. A snapshot: refresh to recount.
          </Text>
        </div>
        <Group gap="xs">
          {data ? (
            <Badge variant="light" color="gray" tt="none">
              Fetched {formatTimestamp(data.fetchedAt)}
            </Badge>
          ) : null}
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={refreshing}
            onClick={refresh}
          >
            Refresh counts
          </Button>
        </Group>
      </Group>
      {error ? (
        <Alert color="red" variant="light" title="Could not count the fleet">
          {error.message}
        </Alert>
      ) : loading && !data ? (
        <LoadingState label="Counting wallets…" />
      ) : data?.unavailable ? (
        <KeyInventoryErrorState error={data.unavailable} />
      ) : data ? (
        <Stack gap="sm">
          <Table striped withTableBorder verticalSpacing="xs" data-testid="generation-counts">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Generation</Table.Th>
                <Table.Th>Wallets</Table.Th>
                <Table.Th>Exposed</Table.Th>
                {FLEET_WALLET_STATES.map((state) => (
                  <Table.Th key={state} tt="capitalize">
                    {state}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.rows.map((row) => (
                <Table.Tr key={row.key ?? "unknown"}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <GenerationBadge generation={row.key} />
                      {row.deprecated ? (
                        <Text size="xs" c="dimmed">
                          deprecated
                        </Text>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <CountCell result={row.total} />
                  </Table.Td>
                  <Table.Td>
                    {row.exposed ? <CountCell result={row.exposed} /> : <Text c="dimmed">—</Text>}
                  </Table.Td>
                  {FLEET_WALLET_STATES.map((state) => (
                    <Table.Td key={state}>
                      {row.byState ? (
                        <CountCell result={row.byState[state]} />
                      ) : (
                        <Text c="dimmed">—</Text>
                      )}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group gap="lg">
            <Text size="sm">
              Wallets with an exposed (1271-capable) key:{" "}
              <Text
                span
                fw={700}
                c={data.legacyWallets.ok && data.legacyWallets.value > 0 ? "red" : undefined}
              >
                {countText(data.legacyWallets)}
              </Text>
            </Text>
            <Text size="sm">
              Exposed keys fleet-wide:{" "}
              <Text
                span
                fw={700}
                c={data.exposedKeys.ok && data.exposedKeys.value > 0 ? "red" : undefined}
              >
                {countText(data.exposedKeys)}
              </Text>{" "}
              ({countText(data.exposedKeysRevoked)} revoked, still signature-capable until KMS
              destruction)
            </Text>
            <Text size="sm">
              Upgrade offers: {countText(data.offers.offered)} offered ·{" "}
              {countText(data.offers.accepted)} accepted · {countText(data.offers.consumed)}{" "}
              consumed · {countText(data.offers.withdrawn)} withdrawn
            </Text>
            <Text size="sm" c="dimmed">
              Inventory coverage: {countText(data.inventoriesWithWallet)} of{" "}
              {countText(data.wallets)} wallets carry their wallet fields (
              {countText(data.inventories)} summaries). A gap means the projection backfill has not
              run or a row was dropped.
            </Text>
          </Group>
        </Stack>
      ) : null}
    </Card>
  );
}

function fleetColumns() {
  return [
    col<FleetWalletRow>({
      id: "wallet",
      header: "Wallet",
      value: (r) => r.id,
      cell: (r) => <RelatedLink entity="wallet" id={r.id} />,
    }),
    col<FleetWalletRow>({
      id: "generation",
      header: "Generation",
      value: (r) => r.generation ?? "",
      cell: (r) => <GenerationBadge generation={r.generation} />,
    }),
    col<FleetWalletRow>({
      id: "state",
      header: "Wallet state",
      value: (r) => r.state ?? "",
      cell: (r) => <StatusBadge state={r.state} />,
    }),
    col<FleetWalletRow>({
      id: "legacyKeys",
      header: "Exposed keys",
      value: (r) => r.legacyKeys,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <Badge
            color={r.legacyKeys > 0 ? "red" : "gray"}
            variant={r.legacyKeys > 0 ? "filled" : "light"}
          >
            {r.legacyKeys}
          </Badge>
          {r.revokedExposedKeys > 0 ? (
            <Tooltip
              label="Revoked off-chain, still signature-capable until their KMS destruction is scheduled."
              multiline
              w={280}
              withArrow
            >
              <Text size="xs" c="red">
                {r.revokedExposedKeys} revoked
              </Text>
            </Tooltip>
          ) : null}
        </Group>
      ),
    }),
    col<FleetWalletRow>({
      id: "legacyTypes",
      header: "Exposed key types",
      value: (r) => r.legacyKeyTypes.join(", "),
      cell: (r) => (
        <Text size="xs" ff="monospace" c={r.legacyKeyTypes.length ? undefined : "dimmed"}>
          {r.legacyKeyTypes.length ? r.legacyKeyTypes.join(", ") : "—"}
        </Text>
      ),
    }),
    col<FleetWalletRow>({
      id: "enabledKeys",
      header: "Enabled keys",
      value: (r) => r.enabledKeys,
      cell: (r) => <Text size="sm">{r.enabledKeys}</Text>,
    }),
    col<FleetWalletRow>({
      id: "main",
      header: "Main",
      value: (r) => r.mainMicroUsdc ?? -1,
      cell: (r) => <Money micro={r.mainMicroUsdc} size="sm" />,
    }),
    col<FleetWalletRow>({
      id: "reserve",
      header: "Reserve",
      value: (r) => r.reserveMicroUsdc ?? -1,
      cell: (r) => <Money micro={r.reserveMicroUsdc} size="sm" />,
    }),
    col<FleetWalletRow>({
      id: "spendable",
      header: "Spendable",
      value: (r) => r.spendableMicroUsdc ?? -1,
      cell: (r) => <Money micro={r.spendableMicroUsdc} size="sm" />,
    }),
    col<FleetWalletRow>({
      id: "exposed",
      header: "Exposed",
      value: (r) => r.exposedMicroUsdc ?? -1,
      cell: (r) => <Money micro={r.exposedMicroUsdc} size="sm" fw={700} />,
    }),
    col<FleetWalletRow>({
      id: "owner",
      header: "User",
      value: (r) => r.inventory.owner ?? "",
      cell: (r) => <RelatedLink entity="user" id={r.inventory.owner} />,
    }),
    col<FleetWalletRow>({
      id: "updatedAt",
      header: "Updated",
      value: (r) => r.updatedAt ?? "",
      cell: (r) => <Timestamp value={r.updatedAt} />,
    }),
  ];
}

const COLUMNS = fleetColumns();

/**
 * A page of key inventories joined to their `walletAvailability` balances (`documentId() in`,
 * 30 ids per listener, never one read per row).
 */
function InventoryTable({
  filters,
  orderBy,
  emptyTitle,
  emptyDescription,
  showExposure,
}: {
  filters: Filter[];
  orderBy: [string, "asc" | "desc"][];
  emptyTitle: string;
  emptyDescription: string;
  showExposure: boolean;
}) {
  const navigate = useNavigate();
  const inventories = usePaginatedQuery<WalletKeyInventoryDoc>(
    COLLECTIONS.walletKeyInventories,
    { filters, orderBy },
    50,
  );
  const balances = useFirestoreInQuery<WalletAvailabilityDoc>(
    COLLECTIONS.walletAvailability,
    DOCUMENT_ID,
    inventories.data.map((row) => row._docId),
  );
  const joined = joinInventoryBalances(inventories.data, balances.data);
  const rows = showExposure ? sortByExposure(joined) : joined;
  const exposure = totalExposure(rows);

  if (inventories.error) return <KeyInventoryErrorState error={inventories.error} />;
  return (
    <Stack gap="sm">
      {balances.error ? (
        <Alert color="orange" variant="light" title="Balances unavailable">
          The wallets below are listed without their walletAvailability balances:{" "}
          {balances.error.message}
        </Alert>
      ) : null}
      {showExposure && rows.length > 0 ? (
        <Text size="sm">
          Exposed across the {rows.length} loaded wallet{rows.length === 1 ? "" : "s"}:{" "}
          <Text span fw={700} ff="monospace">
            {formatMicroUsdc(exposure.exposedMicroUsdc)}
          </Text>
          {exposure.withoutBalance > 0 ? (
            <Text span c="dimmed">
              {" "}
              ({exposure.withoutBalance} without a balance snapshot, not included)
            </Text>
          ) : null}
        </Text>
      ) : null}
      <DataTable
        data={rows}
        columns={COLUMNS}
        loading={inventories.loading || balances.loading}
        hasMore={inventories.hasMore}
        onLoadMore={inventories.loadMore}
        searchPlaceholder="Filter wallets…"
        onRowClick={(row) => navigate(entityPath("wallet", row.id))}
        emptyState={<EmptyState title={emptyTitle} description={emptyDescription} />}
      />
    </Stack>
  );
}

/**
 * The exposed-wallet list's scopes, each on a declared index: all (single-field), provisioned +
 * migrating (`walletState, legacySignatureCapableKeyCount desc`), one generation
 * (`contractGeneration, legacySignatureCapableKeyCount desc`, wallet-service 0.75.0).
 */
function exposureFilters(scope: string): Filter[] {
  const exposed: Filter = ["legacySignatureCapableKeyCount", ">", 0];
  if (scope === "live") return [["walletState", "in", LIVE_WALLET_STATES], exposed];
  if (scope.startsWith("gen:")) return [["contractGeneration", "==", scope.slice(4)], exposed];
  return [exposed];
}

function LegacyExposureCard() {
  const [scope, setScope] = useState<string>("all");
  const filters = exposureFilters(scope);
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs" align="flex-start">
        <div>
          <Text fw={600}>Wallets with exposed (1271-capable) keys</Text>
          <Text size="sm" c="dimmed" maw={760}>
            An exposed key&apos;s permission answers ERC-1271 for its account and its KMS material
            is not scheduled for destruction, whatever its state: a compromised backend could sign
            that account&apos;s USDC away. Revoked keys count until their destruction is scheduled.
            Exposed USDC = the balance of each account (Main / Reserve) carrying such a key. Sorted
            by key count, then exposure. These are the v3.8 migration candidates. A migrated source
            keeps its keys (and any late inflow).
          </Text>
        </div>
        <SegmentedControl
          size="xs"
          value={scope}
          onChange={setScope}
          data={[
            { value: "all", label: "All wallets" },
            { value: "live", label: "Provisioned + migrating" },
            ...TAB_ORDER.map((g) => ({ value: `gen:${g.key}`, label: g.key })),
          ]}
        />
      </Group>
      <InventoryTable
        key={scope}
        filters={filters}
        orderBy={[["legacySignatureCapableKeyCount", "desc"]]}
        emptyTitle="No exposed wallets"
        emptyDescription="No wallet has a session key whose permission answers ERC-1271 with live KMS material."
        showExposure
      />
    </Card>
  );
}

const EXPOSED_KEY_COLUMNS = sessionKeyPermissionColumns({ withWallet: true });

/**
 * Every exposed key fleet-wide (`sessionKeyPermissions where legacySignatureCapable == true
 * order by createdAt desc`, on the 0.75.0 index), revoked ones included. Read from the permission
 * documents because only they carry `kmsKeyDestroyedAt`.
 */
function ExposedKeysCard() {
  const navigate = useNavigate();
  const keys = usePaginatedQuery<SessionKeyPermissionDoc>(
    COLLECTIONS.sessionKeyPermissions,
    { filters: [["legacySignatureCapable", "==", true]], orderBy: [["createdAt", "desc"]] },
    50,
  );
  return (
    <Card withBorder radius="md" padding="lg">
      <Text fw={600}>Exposed keys fleet-wide</Text>
      <Text size="sm" c="dimmed" mb="md" maw={760}>
        Each session key that can still sign an ERC-1271 authorization for its account&apos;s USDC,
        enabled or revoked. A revoked key leaves this list once its KMS destruction is scheduled
        (the teardown follows every revocation: one that lingers here needs its KMS status checked).
      </Text>
      {keys.error ? (
        <KeyInventoryErrorState error={keys.error} />
      ) : (
        <DataTable
          data={keys.data}
          columns={EXPOSED_KEY_COLUMNS}
          loading={keys.loading}
          hasMore={keys.hasMore}
          onLoadMore={keys.loadMore}
          searchPlaceholder="Filter keys…"
          onRowClick={(row) => navigate(entityPath("sessionKey", row.id ?? row._docId))}
          emptyState={
            <EmptyState title="No exposed keys" description="No session key is 1271-capable." />
          }
        />
      )}
    </Card>
  );
}

function offerColumns() {
  return [
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "wallet",
      header: "Wallet",
      value: (r) => r._docId,
      cell: (r) => <RelatedLink entity="wallet" id={r._docId} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "state",
      header: "State",
      value: (r) => r.state,
      cell: (r) => <StatusBadge state={r.state} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "generations",
      header: "Upgrade",
      value: (r) => `${r.fromGeneration} → ${r.toGeneration}`,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <GenerationBadge generation={r.fromGeneration} />
          <Text size="sm">→</Text>
          <GenerationBadge generation={r.toGeneration} />
        </Group>
      ),
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "offeredBy",
      header: "Offered by",
      value: (r) => r.offeredBy,
      cell: (r) => <RelatedLink entity="user" id={r.offeredBy} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "reason",
      header: "Reason",
      value: (r) => r.offerReason,
      cell: (r) => (
        <Tooltip label={r.offerReason} multiline w={320} withArrow disabled={!r.offerReason}>
          <Text size="sm" maw={220} truncate>
            {r.offerReason || "—"}
          </Text>
        </Tooltip>
      ),
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "offeredAt",
      header: "Offered",
      value: (r) => r.offeredAt,
      cell: (r) => <Timestamp value={r.offeredAt} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "acceptedAt",
      header: "Accepted",
      value: (r) => r.acceptedAt ?? "",
      cell: (r) => <Timestamp value={r.acceptedAt} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "endedAt",
      header: "Consumed / withdrawn",
      value: (r) => offerEndedAt(r) ?? "",
      cell: (r) => <Timestamp value={offerEndedAt(r)} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "migration",
      header: "Migration",
      value: (r) => r.migration ?? "",
      cell: (r) => <RelatedLink entity="walletMigration" id={r.migration} />,
    }),
    col<WithDocId<WalletUpgradeOfferDoc>>({
      id: "updatedAt",
      header: "Updated",
      value: (r) => r.updatedAt,
      cell: (r) => <Timestamp value={r.updatedAt} />,
    }),
  ];
}

const OFFER_COLUMNS = offerColumns();

/**
 * Upgrade offers by state (`walletUpgradeOffers where state in [...] order by updatedAt desc`,
 * index `state, updatedAt desc`, wallet-service 0.75.0). Offer and Withdraw live on Wallet 360.
 */
function UpgradeOffersCard() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(OFFER_LIST_FILTERS[0].value);
  const states = OFFER_LIST_FILTERS.find((f) => f.value === filter)?.states ?? ["offered"];
  const offers = usePaginatedQuery<WalletUpgradeOfferDoc>(
    COLLECTIONS.walletUpgradeOffers,
    { filters: [["state", "in", states]], orderBy: [["updatedAt", "desc"]] },
    50,
  );
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs" align="flex-start">
        <div>
          <Text fw={600}>Upgrade offers</Text>
          <Text size="sm" c="dimmed" maw={760}>
            The case-by-case v3.7 → v3.8 offers. Offered: the owner may start the upgrade. Accepted:
            its migration is moving. A failed or abandoned migration returns the offer to offered;
            withdraw it on Wallet 360 to stop the owner retrying.
          </Text>
        </div>
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={setFilter}
          data={OFFER_LIST_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
        />
      </Group>
      {offers.error ? (
        <UpgradeOffersErrorState error={offers.error} />
      ) : (
        <DataTable
          data={offers.data}
          columns={OFFER_COLUMNS}
          loading={offers.loading}
          hasMore={offers.hasMore}
          onLoadMore={offers.loadMore}
          searchPlaceholder="Filter offers…"
          onRowClick={(row) => navigate(entityPath("wallet", row._docId))}
          emptyState={
            <EmptyState title="No offers" description="No upgrade offer is in this state." />
          }
        />
      )}
    </Card>
  );
}

function GenerationListsCard() {
  return (
    <Card withBorder radius="md" padding="md">
      <Text fw={600} mb="xs">
        Wallets per generation
      </Text>
      <Tabs defaultValue={TAB_ORDER[0].key} keepMounted={false}>
        <Tabs.List>
          {TAB_ORDER.map((g) => (
            <Tabs.Tab key={g.key} value={g.key}>
              {g.key}
              {g.deprecated ? " (deprecated)" : ""}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {TAB_ORDER.map((g) => (
          <Tabs.Panel key={g.key} value={g.key} pt="md">
            <InventoryTable
              filters={[["contractGeneration", "==", g.key]]}
              orderBy={[["updatedAt", "desc"]]}
              emptyTitle={`No ${g.key} wallets`}
              emptyDescription="No key inventory names this generation."
              showExposure={false}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Card>
  );
}

/**
 * Fleet view of smart-contract generations (v3.7 and v3.8 coexist): counts per generation, the
 * wallets with exposed (ERC-1271-capable) session keys and the USDC they expose, every exposed key
 * fleet-wide, and a per-generation wallet list. Reads the admin-only key inventory
 * (wallet-service 0.74.0).
 */
export function GenerationsPage() {
  const counts = useQuery({
    queryKey: ["generationCounts"],
    queryFn: () => fetchGenerationCounts(),
    staleTime: Infinity,
    retry: false,
  });
  return (
    <>
      <PageHeader
        title="Contract generations"
        subtitle="Which smart-contract generation each wallet runs, and which session keys can still sign ERC-1271 authorizations."
      />
      <Stack gap="lg">
        <FleetCountsCard
          data={counts.data}
          loading={counts.isLoading}
          refreshing={counts.isFetching}
          error={counts.error}
          refresh={() => void counts.refetch()}
        />
        <LegacyExposureCard />
        <ExposedKeysCard />
        <UpgradeOffersCard />
        <WalletDestinationLookup />
        <GenerationListsCard />
      </Stack>
    </>
  );
}
